import { ExclamationCircleFilled } from "@ant-design/icons";
import {
  Alert,
  Button,
  Collapse,
  ConfigProvider,
  Modal,
  Progress,
  Spin,
  Table,
  Tabs,
} from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import SideBar from "../../../../components/SideBar";
import NavBar from "../../../../components/NavBar";
import Footer from "../../../../components/Footer";
import axiosInstance from "../../../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../../../variables/constants";
import { frenchDate } from "../../../../variables/functions";
import { rightsService } from "../../../../services/rightsService";
import frFR from "antd/locale/fr_FR";
import { searchLotAttestation } from "../../../../hooks/useSearch";
import { highlightMatch } from "../../../../variables/stringFunctions";

const { confirm } = Modal;

const DetailsLotAttestations = () => {
  const [lots, setLots] = useState([]);
  const [compagnie, setCompagnie] = useState({});
  const [affectationModalOpen, setAffectationModalOpen] = useState(false);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [firstAttestation, setFirstAttestation] = useState(0);
  const [lastAttestation, setLastAttestation] = useState(0);
  const [nombre, setNombre] = useState(0);
  const [reload, setReload] = useState(false);
  const [selectedTypeAttestation, setSelectedTypeAttestation] =
    useState("SIMPLE");
  const [selectedSerieAttestation, setSelectedSerieAttestation] = useState("A");
  const [commentaire, setCommentaire] = useState("");
  const [getLotLoading, setGetLotLoading] = useState(false);

  const [typeAttestations, setTypeAttestations] = useState([]);
  const [serieAttestations, setSerieAttestations] = useState(["A", "B", "C", "D"]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchValue, setSearchValue] = useState("");

  const [usedLots, setUsedLots] = useState([]);
  const [currentLots, setCurrentLots] = useState([]);
  const [existingAttestation, setExistingAttestation] = useState("");
  const [nonValidatedLots, setNonValidatedLots] = useState([]);
  const [validatedLots, setValidatedLots] = useState([]);
  const [dispatchedLots, setDispatchedLots] = useState([]);

  // Pagination: choix 10 / 20 / 50 / 100
  const [pageSize, setPageSize] = useState(10);
  const paginationProps = {
    pageSize,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    onChange: (_page, size) => setPageSize(size),
    showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total}`,
  };

  const param = useParams();
  const navigate = useNavigate();

  // Chargement des données (lots, compagnie, types)
  useEffect(() => {
    const code =
      param.codeCompagnie ?? LOGGED_USER?.profil?.organisme?.code;
    const isCourtier = LOGGED_USER?.agence?.typeAgence === 'COURTIER';

    setGetLotLoading(true);

    Promise.all([
      axiosInstance.get(isCourtier ? `/api/dispatched-lot-attestation/compagnie` : `/api/lot-attestation/compagnie/sql/${code}`),
      axiosInstance.get(`/api/organisme/code/${code}`),
      // Adapte l’URL si ton backend diffère, sinon on garde un fallback
      axiosInstance.get(`/api/produit-attestation`).catch(() => ({ data: [] })),
    ])
      .then(([lotsRes, orgRes, typesRes]) => {
        console.log(lotsRes);
        setLots(Array.isArray(lotsRes.data) ? lotsRes.data : []);
        setUsedLots(lotsRes.data ? lotsRes.data?.filter((l) => l?.dateReception && l?.used) : []);
        setCurrentLots(lotsRes.data ? lotsRes.data?.filter((l) => l?.dateReception && l?.inUse) : []);
        setValidatedLots(lotsRes.data ? lotsRes.data?.filter((l) => l?.dateReception) : []);
        setNonValidatedLots(lotsRes.data ? lotsRes.data?.filter((l) => !l?.dateReception) : []);
        setDispatchedLots(lotsRes.data ? lotsRes.data?.filter((l) => l?.dispatched) : []);

        setCompagnie(orgRes?.data ?? {});
        // si le type par défaut n'existe pas, on prend le 1er
        if (!typesRes.data.find((t) => t.codeId === selectedTypeAttestation)) {
          setSelectedTypeAttestation(typesRes.data[0]?.codeId);
        }
        setTypeAttestations(typesRes.data);
        setReload(false);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => setGetLotLoading(false));
  }, [reload, param.codeCompagnie]);

  const onSubmit = () => {
    setLoading(true);
    if (nombre === lastAttestation - firstAttestation + 1) {
      let lot = {
        compagnie: compagnie,
        lot: [firstAttestation, lastAttestation],
        updatedLot: [firstAttestation, lastAttestation],
        typeAttestation: typeAttestations?.find(
          (element) => element.codeId === selectedTypeAttestation
        ),
        serie: selectedSerieAttestation,
      };

      axiosInstance
        .post(`/api/lot-attestation`, JSON.stringify(lot))
        .then((res) => {
          if (res.status === 226) {
            toast("Ce numéro d'attestation existe déjà!");
            setExistingAttestation(res.data);
          } else {
            setFirstAttestation(0);
            setLastAttestation(0);
            setNombre(0);
            setAffectationModalOpen(false);
            setReload(true);
            toast("Lot d'attestation ajoutée avec succès");
          }
        })
        .catch((err) => {
          console.log(err);
          if (err?.response?.status === 226) {
            toast("Ce numéro d'attestation existe déjà!");
          } else {
            toast("Erreur lors de l'ajout du lot d'attestation");
          }
        })
        .finally(() => setLoading(false));
    } else {
      toast("Veuillez vérifier les valeurs que vous avez saisi!");
      setLoading(false);
    }
  };

  const onSubmitCommandeAttestation = () => {
    if (nombre) {
      let bonCommande = {
        commentaire: commentaire,
        nombre: nombre,
        typeAttestation: typeAttestations?.find(
          (element) => element.codeId === selectedTypeAttestation
        ),
        statutCommande: "INITIE",
      };

      setLoading(true);
      axiosInstance
        .post(`/api/bon-commande`, JSON.stringify(bonCommande))
        .then((res) => {
          if (res.status === 226) {
            toast("Ce numéro d'attestation existe déjà!");
          } else {
            setAffectationModalOpen(false);
            setReload(true);
            toast("Lot d'attestation ajoutée avec succès");
          }
          setOrderModalOpen(false);
        })
        .catch((err) => {
          console.log(err);
          if (err?.response?.status === 226) {
            toast("Ce numéro d'attestation existe déjà!");
          } else {
            toast("Erreur lors de l'ajout du lot d'attestation");
          }
        })
        .finally(() => setLoading(false));
    } else {
      toast(
        "Veuillez renseigner le nombre d'attestation que vous voulez commander!"
      );
    }
  };

  const showReceptionAttestationConfirm = (lotA) => {
    confirm({
      title: "Etes-vous sûr de vouloir valider la réception du lot?",
      icon: <ExclamationCircleFilled />,
      content: "Toutes vos actions sont historisées",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk() {
        validationReception(lotA);
      },
      onCancel() { },
    });
  };

  const validationReception = (lotA) => {
    axiosInstance
      .get(`/api/lot-attestation/reception/${lotA}`)
      .then(() => {
        window.location.reload();
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // Colonnes du tableau
  const columns =
    LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL"
      ? [
        {
          title: "Lot",
          dataIndex: "lot",
          key: "lot",
          render: (_, record) => (
            <h6>
              {`${record.firstAttestation} - ${record.lastAttestation} (${record.serie})`}
            </h6>
          ),
          align: "center",
        },
        {
          title: "Date de livraison",
          dataIndex: "dateReception",
          key: "dateReception",
          render: (_, record) => (
            <p
              style={{
                margin: 0,
                color: record?.dateReception ? "#52c41a" : "#faad14",
              }}
            >
              {record?.dateReception
                ? `Livré le ${frenchDate(record.dateReception)}`
                : "Pas encore livré"}
            </p>
          ),
          align: "center",
        },
        {
          title: "Total",
          dataIndex: "total",
          key: "total",
          render: (_, record) => (
            <p style={{ margin: 0, color: "blue" }}>
              {record?.lotLength || 0}
            </p>
          ),
          align: "center",
        },
        {
          title: "Nbre utilisés",
          dataIndex: "nbreUtilises",
          key: "nbreUtilises",
          render: (_, record) => {
            const lotLen = record?.lotLength || 0;
            const updLen = record?.updatedLotLength || 0;
            const dmgLen = record?.damagedLotLength || 0;
            return (
              <p style={{ margin: 0, color: "blue" }}>
                {lotLen - updLen - dmgLen}
              </p>
            );
          },
          align: "center",
        },
        {
          title: "Attestations endommagées",
          dataIndex: "attestationsEndommagees",
          key: "attestationsEndommagees",
          render: (_, record) => (
            <p style={{ margin: 0, color: "blue" }}>
              {record?.damagedLotLength || 0}
            </p>
          ),
          align: "center",
        },
        {
          title: "Nbre restants",
          dataIndex: "nbreRestants",
          key: "nbreRestants",
          render: (_, record) => (
            <p style={{ margin: 0, color: "blue" }}>
              {record?.updatedLotLength || 0}
            </p>
          ),
          align: "center",
        },
        {
          title: "Pourcentage",
          dataIndex: "pourcentage",
          key: "pourcentage",
          render: (_, record) => {
            const lotLen = record?.lotLength || 0;
            const updLen = record?.updatedLotLength || 0;
            const percent = lotLen
              ? Number((((lotLen - updLen) * 100) / lotLen).toFixed(2))
              : 0;
            return (
              <Progress
                type="dashboard"
                size={50}
                percent={percent}
                strokeColor="blueviolet"
              />
            );
          },
          align: "center",
        },
        // (OPTIONNEL) action visible côté POOL uniquement : Valider réception
        {
          title: "",
          key: "actions",
          render: (_, record) => (
            <div>
              {!record?.dateReception && rightsService("HLA064") ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => showReceptionAttestationConfirm(record.lotAttestationId)}
                >
                  Valider réception
                </button>
              ) : null}
            </div>
          ),
        },
      ]
      : [
        {
          title: "Lot",
          dataIndex: "lot",
          key: "lot",
          render: (_, record) => (
            <h6>
              {`${record.firstAttestation} - ${record.lastAttestation}`}
            </h6>
          ),
          align: "center",
        },
        {
          title: "Date de livraison",
          dataIndex: "dateReception",
          key: "dateReception",
          render: (_, record) => (
            <p
              style={{
                margin: 0,
                color: record?.dateReception ? "#52c41a" : "#faad14",
              }}
            >
              {record?.dateReception
                ? `Livré le ${frenchDate(record.dateReception)}`
                : "Pas encore livré"}
            </p>
          ),
          align: "center",
        },
        {
          title: "Total",
          dataIndex: "total",
          key: "total",
          render: (_, record) => (
            <p style={{ margin: 0, color: "blue" }}>
              {record?.lotLength || 0}
            </p>
          ),
          align: "center",
        },
        {
          title: "Nbre utilisés",
          dataIndex: "nbreUtilises",
          key: "nbreUtilises",
          render: (_, record) => {
            const lotLen = record?.lotLength || 0;
            const updLen = record?.updatedLotLength || 0;
            const dmgLen = record?.damagedLotLength || 0;
            return (
              <p style={{ margin: 0, color: "blue" }}>
                {lotLen - updLen - dmgLen}
              </p>
            );
          },
          align: "center",
        },
        {
          title: "Attestations endommagées",
          dataIndex: "attestationsEndommagees",
          key: "attestationsEndommagees",
          render: (_, record) => (
            <p style={{ margin: 0, color: "blue" }}>
              {record?.damagedLotLength || 0}
            </p>
          ),
          align: "center",
        },
        {
          title: "Nbre restants",
          dataIndex: "nbreRestants",
          key: "nbreRestants",
          render: (_, record) => (
            <p style={{ margin: 0, color: "blue" }}>
              {record?.updatedLotLength || 0}
            </p>
          ),
          align: "center",
        },
        {
          title: "Pourcentage",
          dataIndex: "pourcentage",
          key: "pourcentage",
          render: (_, record) => {
            const lotLen = record?.lotLength || 0;
            const updLen = record?.updatedLotLength || 0;
            const percent = lotLen
              ? Number((((lotLen - updLen) * 100) / lotLen).toFixed(2))
              : 0;
            return (
              <Progress
                type="dashboard"
                size={50}
                percent={percent}
                strokeColor="blueviolet"
              />
            );
          },
          align: "center",
        },
        // IMPORTANT : côté Compagnie, on NE montre PAS "Valider réception"
        {
          title: "",
          dataIndex: "date",
          key: "actions",
          render: (_, record) => (
            <div>
              {!record?.dateReception ? (
                // Pas de bouton, juste un statut
                <button
                  type="button"
                  class="btn btn-primary"
                  onClick={() => {
                    showReceptionAttestationConfirm(record.lotAttestationId);
                  }}
                >
                  Valider réception
                </button>
              ) : rightsService("HLA063") ? (
                record?.updatedLotLength > 0 ? (
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={() => {
                      navigate(`/dispatching-lot-attestation/${record.lotAttestationId}`);
                    }}
                  >
                    Dispatcher <i className="bx bx-chevron-right"></i>
                  </button>
                ) : (
                  <Button
                    type="primary"
                    onClick={() => {
                      navigate(`/dispatching-lot-attestation/${record.lotAttestationId}`);
                    }}
                  >
                    Détails
                  </Button>
                )
              ) : null}
            </div>
          ),
        },
      ];


  const renderSearchResults = (results) => {
    return <div className="px-3 py-2">
      {results.map((item, index) => (
        <div
          key={index}
          onClick={() =>
            navigate(`/dispatching-lot-attestation/${item.codeId}`)
          }
          className="dropdown-item d-flex align-items-center gap-2 py-1"
          style={{ cursor: "pointer" }}
        >
          <span className="text-truncate">
            Lot d'{" "}
            {[
              item?.typeAttestation,
              `[${item?.lot1} - ${item?.lot2}]`,
              item?.compagnie,
            ].map((text, i) => (
              <React.Fragment key={i}>
                {i > 0 && " - "}
                {highlightMatch(text?.toString(), searchValue)}
              </React.Fragment>
            ))}
          </span>
        </div>
      ))}
    </div>;
  }

  useEffect(() => {
    if (searchValue && searchValue?.length > 2) {
      searchLotAttestation(searchValue).then((res) => {
        setSearchResults(res.data);
      }).catch((err) => {
        console.log(err);
      });
    }
  }, [searchValue]);

  return (
    <div class="layout-wrapper layout-content-navbar">
      <div class="layout-container">
        <SideBar />

        {/* <!-- Layout container --> */}
        <div class="layout-page">
          <NavBar
            filteredResults={searchResults}
            setFilteredResults={setSearchResults}
            renderSearchResults={renderSearchResults}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />

          {/* <!-- Content wrapper --> */}
          <div class="content-wrapper">
            {/* <!-- Content --> */}
            <div class="container-xxl flex-grow-1 container-p-y">
              <div class="row g-6 mb-6">
                <div class="col-lg-12 col-xxl-12">
                  <Spin spinning={getLotLoading}>
                    <div class="card h-100">
                      <div class="card-header d-flex align-items-center justify-content-between">
                        <div class="card-title mb-0">
                          <h5 class="m-0 me-2">
                            Les lots d'attestations de {compagnie?.raisonSociale ?? ""}
                          </h5>
                        </div>

                        {rightsService("HLA061") &&
                          LOGGED_USER?.profil?.organisme?.typeOrganisme ===
                          "POOL" ? (
                          <div class="dropdown">
                            <button
                              type="button"
                              class="btn btn-primary"
                              onClick={() => setAffectationModalOpen(true)}
                            >
                              Affecter un lot
                            </button>
                          </div>
                        ) : null}

                        {rightsService("HLA065") &&
                          LOGGED_USER?.profil?.organisme?.typeOrganisme !==
                          "POOL" ? (
                          <button
                            type="button"
                            class="btn btn-primary"
                            onClick={() => setOrderModalOpen(true)}
                          >
                            Effectuer une commande
                          </button>
                        ) : null}
                      </div>

                      <div class="card-body">
                        <ConfigProvider locale={frFR}>
                          {/* Helper pour résumé + tables */}
                          {(() => {
                            const makeSummary =
                              (dataset) =>
                                (pageData = []) => {
                                  const safeLen = (arr) =>
                                    Array.isArray(arr) ? arr.length : 0;

                                  // Totaux visibles (page courante)
                                  const total = pageData.reduce(
                                    (sum, r) => sum + safeLen(r.lot),
                                    0
                                  );
                                  const used = pageData.reduce(
                                    (sum, r) =>
                                      sum +
                                      (safeLen(r.lot) -
                                        safeLen(r.updatedLot) -
                                        (safeLen(r.damagedLot) || 0)),
                                    0
                                  );
                                  const damaged = pageData.reduce(
                                    (sum, r) =>
                                      sum + (safeLen(r.damagedLot) || 0),
                                    0
                                  );
                                  const remaining = pageData.reduce(
                                    (sum, r) => sum + safeLen(r.updatedLot),
                                    0
                                  );
                                  const percentage = Number(
                                    (((used || 0) * 100) / (total || 1)).toFixed(
                                      2
                                    )
                                  );

                                  // Totaux du dataset complet (toutes pages)
                                  const totalLots = dataset.reduce(
                                    (sum, r) => sum + safeLen(r.lot),
                                    0
                                  );
                                  const totalUsed = dataset.reduce(
                                    (sum, r) =>
                                      sum +
                                      (safeLen(r.lot) -
                                        safeLen(r.updatedLot) -
                                        (safeLen(r.damagedLot) || 0)),
                                    0
                                  );
                                  const totalDamaged = dataset.reduce(
                                    (sum, r) =>
                                      sum + (safeLen(r.damagedLot) || 0),
                                    0
                                  );
                                  const totalRemaining = dataset.reduce(
                                    (sum, r) => sum + safeLen(r.updatedLot),
                                    0
                                  );
                                  const averagePercentage =
                                    dataset.length > 0
                                      ? Number(
                                        (
                                          (100 * totalUsed) /
                                          (totalLots || 1)
                                        ).toFixed(2)
                                      )
                                      : 0;

                                  return (
                                    <>
                                      <Table.Summary.Row
                                        style={{ backgroundColor: "#e6ffed" }}
                                      >
                                        <Table.Summary.Cell index={0} colSpan={2}>
                                          <strong>Total</strong>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell
                                          index={2}
                                          align="center"
                                        >
                                          {total}
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell
                                          index={3}
                                          align="center"
                                        >
                                          {used}
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell
                                          index={4}
                                          align="center"
                                        >
                                          {damaged}
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell
                                          index={5}
                                          align="center"
                                        >
                                          {remaining}
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell
                                          index={6}
                                          align="center"
                                        >
                                          <Progress
                                            type="dashboard"
                                            size={50}
                                            percent={percentage}
                                            strokeColor="blueviolet"
                                          />
                                        </Table.Summary.Cell>
                                        {LOGGED_USER?.profil?.organisme
                                          ?.typeOrganisme !== "POOL" && (
                                            <Table.Summary.Cell index={7} />
                                          )}
                                      </Table.Summary.Row>

                                      <Table.Summary.Row
                                        style={{
                                          backgroundColor: "gray",
                                          color: "white",
                                        }}
                                      >
                                        <Table.Summary.Cell index={0} colSpan={2}>
                                          <strong>Total général</strong>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell
                                          index={2}
                                          align="center"
                                        >
                                          {totalLots}
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell
                                          index={3}
                                          align="center"
                                        >
                                          {totalUsed}
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell
                                          index={4}
                                          align="center"
                                        >
                                          {totalDamaged}
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell
                                          index={5}
                                          align="center"
                                        >
                                          {totalRemaining}
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell
                                          index={6}
                                          align="center"
                                        >
                                          <Progress
                                            type="dashboard"
                                            size={50}
                                            percent={averagePercentage}
                                            strokeColor="blueviolet"
                                          />
                                        </Table.Summary.Cell>
                                        {LOGGED_USER?.profil?.organisme
                                          ?.typeOrganisme !== "POOL" && (
                                            <Table.Summary.Cell index={7} />
                                          )}
                                      </Table.Summary.Row>
                                    </>
                                  );
                                };

                            const LotTable = ({ data }) => (
                              <div>
                                <Collapse
                                  accordion
                                  bordered={false}
                                  style={{
                                    backgroundColor: "#f7f8fa",
                                    borderRadius: 8,
                                    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
                                  }}
                                >
                                  <div className="container">
                                    <Table
                                      columns={columns}
                                      dataSource={data}
                                      pagination={paginationProps}
                                      rowKey={(r) =>
                                        r?.codeId ??
                                        (`${r.firstAttestation}-${r.lastAttestation}`)
                                      }
                                      summary={makeSummary(data)}
                                    />
                                  </div>
                                </Collapse>
                              </div>
                            );

                            return (
                              <Tabs
                                defaultActiveKey="non_utilises"
                                style={{ marginTop: 20 }}
                                items={[
                                  {
                                    key: "non_utilises",
                                    label: "Liste des lots non utilisés",
                                    children: <LotTable data={currentLots} />,
                                  },
                                  {
                                    key: "utilises",
                                    label: "Liste des lots utilisés",
                                    children: <LotTable data={usedLots} />,
                                  },
                                  {
                                    key: "non_valides",
                                    label: "Liste des lots non validés",
                                    children: (
                                      <div>
                                        <Collapse
                                          accordion
                                          bordered={false}
                                          style={{
                                            backgroundColor: "#f7f8fa",
                                            borderRadius: 8,
                                            boxShadow:
                                              "0 4px 10px rgba(0, 0, 0, 0.1)",
                                          }}
                                        >
                                          <div className="container">
                                            <Table
                                              columns={columns}
                                              dataSource={nonValidatedLots}
                                              pagination={paginationProps}
                                              rowKey={(r) =>
                                                r?.codeId ??
                                                (`${r.firstAttestation}-${r.lastAttestation}`)
                                              }
                                              summary={makeSummary(
                                                nonValidatedLots
                                              )}
                                            />
                                          </div>
                                        </Collapse>
                                      </div>
                                    ),
                                  },
                                  {
                                    key: "valides",
                                    label: "Liste des lots validés",
                                    children: <LotTable data={validatedLots} />,
                                  },
                                  {
                                    key: "dispatches",
                                    label: "Liste des lots dispatchés",
                                    children: (
                                      <LotTable data={dispatchedLots} />
                                    ),
                                  },
                                ]}
                              />
                            );
                          })()}
                        </ConfigProvider>
                      </div>
                    </div>
                  </Spin>
                </div>
              </div>
            </div>
            {/* <!-- / Content --> */}

            {/* <!-- Footer --> */}
            <Footer />
            {/* <!-- / Footer --> */}

            <div class="content-backdrop fade"></div>
          </div>
          {/* <!-- Content wrapper --> */}
        </div>
        {/* <!-- / Layout page --> */}
      </div>

      {/* Modal d’affectation */}
      <Modal
        open={affectationModalOpen}
        confirmLoading={loading}
        onOk={onSubmit}
        onCancel={() => {
          setFirstAttestation(0);
          setLastAttestation(0);
          setNombre(0);
          setAffectationModalOpen(false);
        }}
        cancelText="Annuler"
        zIndex={1900}
        centered
      >
        <div className="text-center mb-6">
          <h4 className="address-title mb-2">Nouveau lot d'attestation </h4>
          <p className="address-subtitle">
            Affecter un nouveau lot d'attestation pour{" "}
            {compagnie?.raisonSociale ?? ""}
          </p>
        </div>

        <form id="addNewAddressForm" className="row g-5" onSubmit={onSubmit}>
          {existingAttestation?.lot?.length ? (
            <Alert
              message="Erreur lors de l'affectation du lot d'attestation"
              description={`Un ou plusieurs numéro(s) d'attestation sont déjà affectés dans le lot ${existingAttestation?.lot[0]
                } - ${existingAttestation?.lot[
                existingAttestation?.lot?.length - 1
                ]
                } de ${existingAttestation?.compagnie?.raisonSociale}.`}
              type="error"
              showIcon
            />
          ) : null}

          <div className="col-12 col-md-12">
            <label class="form-label" htmlFor="firstAttestation">
              Veuillez entrer le numéro de la première attestation
            </label>
            <input
              className="form-control"
              id="firstAttestation"
              value={firstAttestation}
              name="firstAttestation"
              onChange={(e) => {
                const v = parseInt(e.target.value || "0", 10);
                setFirstAttestation(v);
                setLastAttestation(v);
              }}
              placeholder="Entrez le numéro de la première attestation"
              type="number"
            />
          </div>

          <div className="col-12 col-md-12">
            <label class="form-label" htmlFor="nombre">
              Le nombre d'attestations
            </label>
            <input
              className="form-control"
              id="nombre"
              value={nombre}
              name="nombre"
              onChange={(e) =>
                setNombre(parseInt(e.target.value || "0", 10))
              }
              placeholder="Entrez le nombre d'attestations"
              type="number"
            />
          </div>

          <div className="col-12 col-md-12">
            <label class="form-label" htmlFor="lastAttestation">
              Veuillez entrer le numéro de la dernière attestation
            </label>
            <input
              className="form-control"
              id="lastAttestation"
              value={lastAttestation}
              name="lastAttestation"
              onChange={(e) =>
                setLastAttestation(parseInt(e.target.value || "0", 10))
              }
              placeholder="Entrez le numéro de la dernière attestation"
              type="number"
            />
          </div>

          <div className="col-12 col-md-12">
            <label class="form-label" htmlFor="typeAttestation">
              Veuillez sélectionner le type d'attestation
            </label>
            <select
              className="form-control"
              id="typeAttestation"
              value={selectedTypeAttestation}
              name="typeAttestation"
              onChange={(e) => setSelectedTypeAttestation(e.target.value)}
              placeholder="Sélectionner un type d'attestation"
            >
              {typeAttestations?.map((type, id) => (
                <option key={id} value={type.codeId}>
                  {type.typeAttestation}
                </option>
              ))}
            </select>
          </div>

          <div className="col-12 col-md-12">
            <label class="form-label" htmlFor="typeAttestation">
              Veuillez sélectionner la série d'attestation
            </label>
            <select
              className="form-control"
              id="typeAttestation"
              value={selectedSerieAttestation}
              name="typeAttestation"
              onChange={(e) => setSelectedSerieAttestation(e.target.value)}
              placeholder="Sélectionner un type d'attestation"
            >
              {serieAttestations?.map((serie, id) => (
                <option key={id} value={serie}>
                  {serie}
                </option>
              ))}
            </select>
          </div>
        </form>
      </Modal>

      {/* Modal de commande */}
      <Modal
        open={orderModalOpen}
        confirmLoading={loading}
        onOk={onSubmitCommandeAttestation}
        onCancel={() => setOrderModalOpen(false)}
        zIndex={1900}
        centered
      >
        <div className="text-center mb-6">
          <h4 className="address-title mb-2">
            Commande de lot d'attestations{" "}
          </h4>
          <p className="address-subtitle">
            Effectuer une commande de lot(s) d'attestations
          </p>
        </div>

        <form
          id="addNewAddressForm"
          className="row g-5"
          onSubmit={onSubmitCommandeAttestation}
        >
          <div className="col-12 col-md-12">
            <label class="form-label" htmlFor="nombreCommande">
              Veuillez entrer le nombre d'attestations
            </label>
            <input
              className="form-control"
              id="nombreCommande"
              value={nombre}
              name="nombreCommande"
              onChange={(e) =>
                setNombre(parseInt(e.target.value || "0", 10))
              }
              placeholder="Entrez le nombre d'attestations que vous voulez commander"
              type="number"
            />
          </div>

          <div className="col-12 col-md-12">
            <label class="form-label" htmlFor="typeAttestationCommande">
              Veuillez sélectionner le type d'attestation
            </label>
            <select
              className="form-control"
              id="typeAttestationCommande"
              value={selectedTypeAttestation}
              name="typeAttestationCommande"
              onChange={(e) => setSelectedTypeAttestation(e.target.value)}
              placeholder="Sélectionner un type d'attestation"
            >
              {typeAttestations?.map((type, id) => (
                <option key={id} value={type.codeId}>
                  {type.typeAttestation}
                </option>
              ))}
            </select>
          </div>

          <div className="col-12 col-md-12">
            <label class="form-label" htmlFor="commentaire">
              Un commentaire ?
            </label>
            <textarea
              className="form-control"
              id="commentaire"
              value={commentaire}
              name="commentaire"
              onChange={(e) => setCommentaire(e.target.value)}
              placeholder="Un commentaire pour des précisions sur la commande"
              rows={3}
            ></textarea>
          </div>
        </form>
      </Modal>

      {/* <!-- Overlay --> */}
      <div class="layout-overlay layout-menu-toggle"></div>
      {/* <!-- Drag Target Area To SlideIn Menu On Small Screens --> */}
      <div class="drag-target"></div>
    </div>
  );
};

export default DetailsLotAttestations;
